import Compose from './Compose';
import Move from './Move';
import KeyBoard from './KeyBoard';
import GameReplay from './GameReplay';
import ApiDebugPanel from './ApiDebugPanel';
import GoogleAuthCallback from './GoogleAuthCallback';
import { ApiTracker } from './ApiTracker';
import AutoPlayer from './AutoPlayer';
import { createPermsWorker } from './permsWorkerClient';
import { useState, useEffect, useRef } from "react";
import { getUnrestrictedLetters, complementUnrestrictedLetters, get_capped_violations, get_mc_violations } from './constraintMath';
import { last } from './arrayHelpers';
import { computeGameScores } from './scoring';
import ShareIcon from './ShareIcon';
import AuthButtons from './AuthButtons';

const fast = 400;
const WS_URL = "wss://stghdyc3fb.execute-api.us-east-2.amazonaws.com/prod";
const GOOGLE_CALLBACK_PATH = "/auth/google/callback";
const is_viewing = /\/view\/([a-z0-9]+)(\/([0-9]+))?/.test(window.location.pathname);
const view_code = is_viewing ? [...window.location.pathname.matchAll(/\/view\/([a-z0-9]+)(\/([0-9]+))?/g)][0][1] : "";
let row_count = (is_viewing ? [...window.location.pathname.matchAll(/\/view\/([a-z0-9]+)(\/([0-9]+))?/g)][0][2] : -1);
row_count = (row_count === undefined || row_count === -1) ? -1 : parseInt(row_count.substring(1));

function App() {
  // WS connection registration routes to `https://<apiPrefix>.prep.ai/ws-validate`
  // (aws/ws-connect-lambda). "dev" = the Cloudflare tunnel to the self-hosted backend, so
  // connectionIds register on the same backend that issued the ticket — otherwise live
  // shared-game pushes never reach the client. See wordchase_host/PRODUCTION.md.
  const WS_API_PREFIX = "dev";
  const [apiPrefix] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const paramApi = params.get("api");
    if (paramApi) {
      localStorage.setItem("apiPrefix", paramApi);
      return paramApi;
    }
    // Heal any stale prefix so clients pinned to a different backend recover on load.
    const stored = localStorage.getItem("apiPrefix");
    if (stored && stored !== WS_API_PREFIX) {
      localStorage.setItem("apiPrefix", WS_API_PREFIX);
      return WS_API_PREFIX;
    }
    return stored || WS_API_PREFIX;
  });
  // Production: same-origin — the API is reverse-proxied at <host>/api by the
  // Cloudflare Pages Function (functions/api/[[path]].js) to api.prep.ai. This needs
  // no env var (so deploys can't ship a broken origin), gives first-party cookies,
  // and means no CORS. Override with REACT_APP_API_ORIGIN for local dev against prod
  // (.env.development.local); localhost falls back to a local backend.
  const origin = process.env.REACT_APP_API_ORIGIN || (window.location.hostname === "localhost" ? "http://localhost:8080" : window.location.origin + "/api");
  const apiBase = origin.replace(/\/$/, "");
  const isGoogleCallbackRoute = window.location.pathname === GOOGLE_CALLBACK_PATH;
  
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const onTouchMove = (e) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    const diffX = e.touches[0].clientX - touchStartX.current;
    const diffY = e.touches[0].clientY - touchStartY.current;

    // Only swipe if horizontal movement is greater than vertical
    if (Math.abs(diffX) > Math.abs(diffY)) {
      if (diffX < -50 && !showDebug) {
        _showDebug(true);
        touchStartX.current = null;
        touchStartY.current = null;
      } else if (diffX > 50 && showDebug) {
        _showDebug(false);
        touchStartX.current = null;
        touchStartY.current = null;
      }
    }
  };

  const onTouchEnd = () => {
    touchStartX.current = null;
    touchStartY.current = null;
  };

  // State and references initialization
  // Boot / in-flight gate: 0 = cold, 1 = a request is in-flight, 2 = ready. A plain
  // ref-as-flag mutated via .current (KeyBoard's make_move reads/writes it too).
  const tristate = useRef(0);
  const [initializing, _initializing] = useState(true);
  const [feeding_back, _feeding_back] = useState(false);
  const [sending_feedback, _sending_feedback] = useState(false);
  const [sharing, _sharing] = useState(false);
  const [hide_i, _hide_i] = useState(0);
  const [disabled, _disabled] = useState(false);
  const [game, _game] = useState({});
  const [clue, _clue] = useState("");
  const [clue_requested, _clue_requested] = useState(false);
  const [fetching_clue, _fetching_clue] = useState(false);
  const [suggestion, _suggestion] = useState("");
  const [fetching_suggestion, _fetching_suggestion] = useState(false);
  const [moveStats, _moveStats] = useState(null);
  // Strategic words (relax-model): the server sends only a boolean (a strategic probe
  // exists) plus relax_positions (the slots a probe overrides). The UI unlocks exactly
  // those slots (blank, any letter, navigable) and otherwise plays normally; the word
  // list is never sent (it would expose optimal probes and compromise integrity).
  const [strategic, _strategic] = useState(false);
  const [relaxPositions, _relaxPositions] = useState([]);
  const relaxPositionsRef = useRef([]);  // latest relaxPositions, readable from async/WS callbacks without stale closures
  // True while the on-screen word is a draft restored from the server (reload). The server's
  // draft_word already has relax slots blanked and the player's letters replayed on top, so the
  // blank-on-arrival effect must leave it alone or the client and server words diverge.
  const draftRestoredRef = useRef(false);
  const [nextWordCount, _nextWordCount] = useState(-1);
  const [magicPattern, _magicPattern] = useState(null);
  const [suggestMode, _suggestMode] = useState(false);
  const [suggestTrigger, _suggestTrigger] = useState(0);
  const [guessed, _guessed] = useState({});
  const [constraints, _constraints] = useState({});
  const [vsize, _vsize] = useState(-1);
  const [handle, _handle] = useState("");
  const [share, _share] = useState("");
  const [feature_unique, _feature_unique] = useState(false);
  const [cursor, _cursor] = useState(0);
  const [word, _word] = useState([]);
  const [original_word, _original_word] = useState([]);
  const [last_key, _last_key] = useState({ t: 0 });
  const [caps, _caps] = useState(false);
  const setCaps = (newCaps) => {
    if (caps === newCaps) return;
    _caps(newCaps);
    if (apiTracker.current) {
      apiTracker.current.trackedFetch(origin + "/settings/keyboard_uppercase", { method: "POST", credentials: "include", body: newCaps.toString() }, 'settings');
    } else {
      fetch(origin + "/settings/keyboard_uppercase", { method: "POST", credentials: "include", body: newCaps.toString() });
    }
  };
  const toggleCaps = () => setCaps(!caps);
  const [show_invalids, _show_invalids] = useState(true);
  const [moves, _moves] = useState(0);
  const [key_strokes, _key_strokes] = useState(0);
  const [touch_strokes, _touch_strokes] = useState(0);
  const [key_jumps, _key_jumps] = useState(0);
  const [click_jumps, _click_jumps] = useState(0);
  const [showDebug, _showDebug] = useState(false);
  const [pre_share, _pre_share] = useState([]);
  const [share_entangled, _share_entangled] = useState(true);
  const [exhausted, _exhausted] = useState(0);
  const [replaying, _replaying] = useState(false);
  const [wsTiming, _wsTiming] = useState(null);
  const [allGamesMmGpa, _allGamesMmGpa] = useState(null);
  const [allGamesMaxAvg, _allGamesMaxAvg] = useState(null);
  const [allGamesAvg, _allGamesAvg] = useState(null);
  const [wsLogs, _wsLogs] = useState([]);
  const [pendingAnalytics, _pendingAnalytics] = useState([]);
  const [analyticsLog, _analyticsLog] = useState([]);
  const analyticsStartTime = useRef(null);
  const analyticsPollTimer = useRef(null);
  const analyticsInflight = useRef(false);
  const analyticsRetrigger = useRef(null);
  const preemptedOptConstraints = useRef(null);
  const preemptedMagicPattern = useRef(undefined);
  const [analyticsDebug, _analyticsDebug] = useState({ inflight: false, lastTrigger: null, lastResult: null, gatedCount: 0 });
  const [apiConnectState, _apiConnectState] = useState("idle");
  const [apiConnectMessage, _apiConnectMessage] = useState("");
  const [oauthState, _oauthState] = useState("idle");
  const [oauthMessage, _oauthMessage] = useState("");
  const [autoPlaying, _autoPlaying] = useState(false);
  const [autoPlayLevel, _autoPlayLevel] = useState(3);
  const [autoPlayStatus, _autoPlayStatus] = useState('idle');
  const autoPlayerRef = useRef(null);
  const kbRef = useRef(null);
  const constraintsRef = useRef(constraints);
  useEffect(() => { constraintsRef.current = constraints; }, [constraints]);
  const pendingAnalyticsRef = useRef([]);  useEffect(() => { pendingAnalyticsRef.current = pendingAnalytics; }, [pendingAnalytics]);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const wsRef = useRef(null);
  const gameRef = useRef(game);
  useEffect(() => { gameRef.current = game; }, [game]);
  const wasComplete = useRef(false);
  const new_game_ref = useRef();
  const end_game_feedback_ref = useRef();
  const end_game_escape_feedback_ref = useRef();
  const end_game_escape_share_ref = useRef();
  const gameSessionIdRef = useRef(Date.now());
  const moveSeqRef = useRef(0);
  const perms_cache = useRef(new Map());
  const [permsAsync, _permsAsync] = useState({ result: null, shadow: [], violations: [[], [], []], feasible: [], infeasible: [], computing: false });
  const last_keystroke_ref = useRef(Promise.resolve());
  const suggestMoveSeq = useRef(0);
  const fetching = useRef(false);
  const pending = useRef(new Array());
  const cache_version = useRef(0);
  const lastSuggestTriggerRef = useRef(0);
  const cache = useRef(new Map());
  const apiTracker = useRef(new ApiTracker({ slowThreshold: 2000, maxEntries: 60 }));
  const [apiDebugVersion, _apiDebugVersion] = useState(0);
  // Wire tracker onChange to force re-render of debug panel
  useEffect(() => {
    apiTracker.current.onChange = () => _apiDebugVersion(v => v + 1);
  }, []);
  const cacher = (new_word, clear_and_skip = false) => {
    _word(new_word)
    if (clear_and_skip) {
      cache.current.clear();
      pending.current = new Array();
      _exhausted(0);
      cache_version.current = cache_version.current + 1;
      cl("cache_version: " + cache_version.current);
      return;
    }
    const word_str = new_word.join("");
    if (word_str === " ".repeat(game.n) || /^[a-z]*$/.test(word_str)) {
      _exhausted(0);
      return;
    }
    if (cache.current.has(word_str)) {
      _exhausted(cache.current.get(word_str));
    } else {
      if (fetching.current) {
        cl("pushing: " + new_word);
        pending.current.push(Array.from(new_word));
      } else {
        _exhausted(-1);
        const f = (w) => {
          w = w.filter(e => !cache.current.has(e))
          w.map(e => cache.current.set(e, -1));
          cl("fetching: " + w);
          fetching.current = true;
          const fetch_cache_version = cache_version.current;
          apiTracker.current.trackedFetch(`${origin}/exhausted`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(w) }, 'exhausted')
            .then(({ data: r }) => {
              if (r && fetch_cache_version === cache_version.current) {
                r.o.map((e, i) => {
                  cl("caching: " + w[i]);
                  cache.current.set(w[i], e);
                  if (word.join("") === w[i]) {
                    _exhausted(e);
                  }
                })
              } else if (!r) {
                cl("exhausted fetch failed");
              } else {
                cl("skipping cache because version changed, fetch_cache_version = " + fetch_cache_version + ", cache_version.current = " + cache_version.current);
              }
              fetching.current = false;
              if (pending.current.length > 0) {
                const pending_words = Array.from(pending.current.map(e => e.join("")));
                pending.current = new Array();
                cl("caching pending: " + pending_words);
                f(pending_words);
              }
            })
            .catch(error => {
              console.error("Error checking exhausted:", error);
              fetching.current = false;
              // Drop only the failed words. (The old code called cache.current.clear(e),
              // but Map.clear ignores its arg and wiped the ENTIRE cache on one failure.)
              w.map(e => cache.current.delete(e));
            });
        }
        f([word_str]);
      }
    }
  }

  // Helper functions and event handlers
  const is_complete = () => game.game_moves.length >= 1 && game.game_moves[game.game_moves.length - 1].reply === "G".repeat(game.n);
  const guessed_data = (guessed === undefined || game.shared) ? undefined : guessed[word.join("")];
  const word_str = word.join("");

  const on_keyup = (e) => {
    if (e.repeat || e.altKey || e.ctrlKey) { }
    else if (e.metaKey) { }
    else if (e.key === "CapsLock") { setCaps(false); }
  };

  const on_keydown = (e) => {
    if (is_viewing) { }
    else if (replaying && e.key === "Escape") { _replaying(false); }
    else if (e.repeat || e.altKey || e.ctrlKey) { }
    else if (e.metaKey && e.key === "Backspace") { kb.delete(); }
    else if (e.shiftKey && e.key.toLowerCase() === 'a') { toggleCaps(); }
    else if (e.metaKey) { }
    else if (e.key === "CapsLock") { setCaps(true); }
    else if (e.key === "Escape") { kb.escape(); }
    else if (e.key === "Backspace") { kb.backspace(); }
    else if (e.key === "ArrowLeft" || e.key === "<") { kb.arrow_left(() => _moves(moves + 1)); }
    else if (e.key === "ArrowRight" || e.key === ">") { kb.arrow_right(() => _moves(moves + 1)); }
    else if (e.key === "Enter") { kb.enter(); }
    else if (e.key === "?") { kb.question(); }
    else if (e.key === "^") { kb.stab(); }
    else if (e.key === "`" || e.key === "~") { _showDebug(!showDebug); }
    else if (e.key.length > 1) { }
    else if (e.key === "*") { _suggestMode(true); _suggestTrigger(t => t + 1); }
    else if (e.key === "-") { _show_invalids(! show_invalids); }
    else if (e.key === " ") { kb.space(); }
    else if (e.key >= '0' && e.key <= '9' || e.key === '$') { kb.digit_dollar(e.key, () => _key_jumps(key_jumps + 1)); }
    else if (e.key === "@") {
      if (autoPlaying) {
        _autoPlaying(false);
        if (autoPlayerRef.current) autoPlayerRef.current.stop();
      } else {
        _autoPlaying(true);
      }
    }
    else { kb.letter(e.key.toLowerCase(), () => _key_strokes(key_strokes + 1), true); }
  };

  function init_cursor (pos) {
    const first = pos.findIndex(e => e.length > 1);
    return first === -1 ? pos.length : first;
  }

  function init_word (pos) {
    const t = Array.from(Array(pos.length).keys()).map(i => pos[i].length === 1 ? pos[i][0] : ' ');
    _original_word(Array.from(t));
    return t;
  }

  const applyGameSnapshot = (o, options = {}) => {
    const { keepCurrentWord = false, resetMoveStats = false } = options;
    if (!o) {
      throw new Error("Missing game snapshot");
    }

    perms_cache.current.clear();
    _game(o.game);
    _clue(o.game.clue);
    _clue_requested(o.game.clue_requested);
    _caps(o.keyboard_uppercase === true);
    if (o.guessed !== undefined) {
      _guessed(o.guessed);
    }

    const complemented_pos = complementUnrestrictedLetters(
      o.pos,
      getUnrestrictedLetters(o.pos, new Map(Object.entries(o.min_counts)), o.capped)
    );

    _constraints({ pos: complemented_pos, min_counts: new Map(Object.entries(o.min_counts)), capped: o.capped });
    _vsize(o.vsize);
    _handle(o.handle);
    _share(o.share);

    const last_move = o.game.game_moves.length > 0 ? o.game.game_moves[o.game.game_moves.length - 1] : null;
    _moveStats(resetMoveStats ? null : (last_move ? {
      move_x_k: last_move.move_x_k || 0,
      word_rank: last_move.word_rank || 0,
      word_count: last_move.word_count || 0,
      word_quartiles: last_move.word_quartiles || [],
      next_word_count: o.next_word_count || -1,
      mm_actual: last_move.mm_actual || 0,
      mm_best: last_move.mm_best || 0,
      mm_worst: last_move.mm_worst || 0,
      mm_rank: last_move.mm_rank || 0,
      mm_total: last_move.mm_total || 0
    } : null));
    _strategic(false); _relaxPositions([]);
    moveSeqRef.current += 1;

    _nextWordCount(o.next_word_count || -1);
    if (o.all_games_avg != null) _allGamesAvg(o.all_games_avg);
    if (o.all_games_mm_avg != null) _allGamesMmGpa(o.all_games_mm_avg);
    if (o.all_games_max_avg != null) _allGamesMaxAvg(o.all_games_max_avg);
    _feature_unique(o.feature_unique);
    _magicPattern(o.magic_pattern || null);

    if (keepCurrentWord && o.draft_word && o.draft_word.replace(/[^ ]/g, '').length < o.draft_word.length) {
      init_word(o.pos);
      _word(Array.from(o.draft_word));
      _cursor(o.draft_cursor);
      draftRestoredRef.current = true;
    } else {
      draftRestoredRef.current = false;
      _word(init_word(o.pos));
      _cursor(init_cursor(o.pos));
    }

    wasComplete.current = o.game.game_moves.length >= 1 &&
      o.game.game_moves[o.game.game_moves.length - 1].reply === "G".repeat(o.game.n);
  };

  const refreshAuthState = async () => {
    const { data } = await apiTracker.current.trackedFetch(
      origin + "/currentgame",
      { method: "POST", credentials: "include" },
      "currentgame-refresh"
    );
    const snapshot = data?.o;
    if (!snapshot) {
      throw new Error("Unable to refresh session");
    }
    applyGameSnapshot(snapshot, { keepCurrentWord: true });
    _initializing(false);
    tristate.current = 2;
    _disabled(false);
    return snapshot;
  };

  // Boot + global key listeners. INTENTIONALLY has no dependency array: it must
  // re-run every render so the keydown/keyup listeners are re-bound with a fresh
  // closure over cursor/strategic/relaxPositions/etc. (a []-deps version would
  // capture stale state and break the physical keyboard). The one-time fetch is
  // guarded by the tristate gate (also survives StrictMode's double-mount), and
  // the cleanup removes the listeners each render. Do not "fix the deps" here.
  useEffect(() => {
    (async () => {
      if (isGoogleCallbackRoute) {
        _initializing(false);
        tristate.current = 2;
        _disabled(false);
        return;
      }
      if (tristate.current === 2) {
        window.addEventListener("keydown", on_keydown);
        window.addEventListener("keyup", on_keyup);
      } else if (tristate.current === 1) {
      } else {
        console.log("fetching");
        tristate.current = 1;
        _disabled(true);
        try {
          let o = null;
          if (is_viewing) {
            o = (await apiTracker.current.trackedFetch(origin + "/view", { method: "POST", credentials: "include", body: view_code }, 'view')).data?.o;
          } else {
            o = (await apiTracker.current.trackedFetch(origin + "/currentgame", { method: "POST", credentials: "include" }, 'currentgame')).data?.o;
          }
          await new Promise(r => setTimeout(r, 9));
          applyGameSnapshot(o, { keepCurrentWord: true });
          // Kick off analytics polling — fetchMoveAnalytics manages its own timer.
          // Clear any poll timer leaked from a previous game/session first.
          if (analyticsPollTimer.current) { clearInterval(analyticsPollTimer.current); analyticsPollTimer.current = null; }
          gameSessionIdRef.current = Date.now();
          analyticsStartTime.current = Date.now();
          fetchMoveAnalytics('load');
          _initializing(false);
          tristate.current = 2;
          _disabled(false);
          setTimeout(() => {
            if (end_game_feedback_ref.current && game.shared && !game.shared.show) { end_game_feedback_ref.current.focus(); }
            else if (new_game_ref.current) { new_game_ref.current.focus(); }
          }, 34);
          console.log("ok");
        } catch (error) {
          console.log("error");
          tristate.current = 0;
          if (error.code === error.ABORT_ERR) {
            console.error("aborted fetch");
          } else {
            console.log(error);
          }
        }
      }
    })();
    if (!isGoogleCallbackRoute &&
        !strategic &&
        constraints.pos !== undefined &&
        constraints.pos.filter(v => v.length > 1).length === 1 &&
        word.every(e => e !== ' ') &&
        kb &&
        !kb.disabled_enter()) {
      cl("auto firing enter");
      _cursor(constraints.pos.findIndex(v => v.length > 1));
      kb.enter();
    }
    return () => {
      window.removeEventListener("keydown", on_keydown);
      window.removeEventListener("keyup", on_keyup);
    }
  });

  useEffect(() => {
    if (!initializing && game.game_moves) {
      const nowComplete = is_complete();
      if (nowComplete && !wasComplete.current) {
        console.log("Game won!");
        wasComplete.current = true;
      }
      wasComplete.current = nowComplete;
    }
  });

  useEffect(() => {
    if (clue) {
      _magicPattern(null);
    }
  }, [clue]);

  // Auto-fetch suggestion when suggestMode is on and word changes
  const wordStr = word.join("");
  const moveCount = game.game_moves ? game.game_moves.length : 0;
  useEffect(() => {
    if (!suggestMode) return;
    // Skip fetch if all typed letters already match the current suggestion (unless explicitly triggered by '*')
    const isExplicitTrigger = suggestTrigger !== lastSuggestTriggerRef.current;
    lastSuggestTriggerRef.current = suggestTrigger;
    if (!isExplicitTrigger && suggestion && !word.some((ch, i) => ch !== ' ' && suggestion[i] !== ch)) return;

    let cancelled = false;
    const seqAtRequest = moveCount;
    suggestMoveSeq.current = seqAtRequest;
    
    const fetchSuggestion = async () => {
      try {
        _fetching_suggestion(true);
        const { data: resp } = await apiTracker.current.trackedFetch(origin + "/suggest", { method: "POST", credentials: "include", body: wordStr }, 'suggest');
        if (cancelled || !resp) return;
        // Discard stale result if move has advanced since request
        if (suggestMoveSeq.current !== seqAtRequest) return;
        const words = resp.o?.words || resp.words || [];
        _suggestion(words.length > 0 ? words[0] : "·".repeat(game.n));
      } catch (error) {
        console.log("suggest error", error);
      } finally {
        if (!cancelled) _fetching_suggestion(false);
      }
    };

    // Minimal debounce for typing to prevent spamming the backend, but execute immediately for explicit triggers
    const delayMs = isExplicitTrigger ? 0 : 250;
    const timer = setTimeout(fetchSuggestion, delayMs);
    
    return () => { 
      cancelled = true; 
      clearTimeout(timer);
    };
  }, [wordStr, suggestMode, suggestTrigger]);
  // Reset suggest mode and clear suggestion on game move
  useEffect(() => {
    _suggestMode(false);
    _suggestion("");
    _fetching_suggestion(false);
  }, [moveCount]);

  // fetchMoveAnalytics: single path for loading analytics from DB.
  // GATED: at most one request in-flight. If called while in-flight, the
  // trigger is saved and a single retry fires when the current request completes.
  const fetchMoveAnalytics = (trigger) => {
    if (trigger === 'move') { _magicPattern(null); _strategic(false); _relaxPositions([]); draftRestoredRef.current = false; }
    if (analyticsInflight.current) {
      // Already in-flight — save the trigger for a retry after completion
      analyticsRetrigger.current = trigger;
      _analyticsDebug(prev => ({ ...prev, gatedCount: prev.gatedCount + 1 }));
      cl(`[analytics] GATED ${trigger} (inflight), will retrigger`);
      return;
    }
    analyticsInflight.current = true;
    analyticsRetrigger.current = null;
    const fetchSessionId = gameSessionIdRef.current;
    const seqAtFetch = moveSeqRef.current;
    _analyticsDebug(prev => ({ ...prev, inflight: true, lastTrigger: trigger }));
    cl(`[analytics] FETCH ${trigger}`);
    const doFetch = async (currentTrigger) => {
      try {
        const elapsed = analyticsStartTime.current ? ((Date.now() - analyticsStartTime.current) / 1000).toFixed(1) : '?';
        const { data: rawData } = await apiTracker.current.trackedFetch(origin + '/move_analytics', { method: 'POST', credentials: 'include' }, 'move_analytics');
        if (gameSessionIdRef.current !== fetchSessionId) {
          cl(`[analytics] Discarding response due to session changed`);
          return;
        }
        if (!rawData) return;
        const a = rawData.o;
        if (!a) return;
        const changes = [];
        // Update game state for last move
        if (a.word_rank > 0 && a.word_count > 0) {
          _game(prev => {
            if (!prev?.game_moves?.length) return prev;
            const moves = [...prev.game_moves];
            moves[moves.length - 1] = { ...moves[moves.length - 1], word_rank: a.word_rank, word_count: a.word_count, vocab_actual: a.vocab_actual, vocab_best: a.vocab_best, vocab_worst: a.vocab_worst, mm_actual: a.mm_actual, mm_best: a.mm_best, mm_worst: a.mm_worst };
            return { ...prev, game_moves: moves };
          });
          changes.push(`vocab r=${a.word_rank}/${a.word_count}`);
        }
        if (a.word_quartiles && a.word_quartiles.length > 0) {
          changes.push(`quartiles=${a.word_quartiles.length}`);
        }
        if (a.mm_actual > 0) {
          changes.push(`minimax ⚔${a.mm_actual} r=${a.mm_rank}/${a.mm_total}`);
        }
        if (a.strategic !== undefined) {
          // Relax-model: the server sends a boolean + the positions to relax, never the word
          // list. Discard if the move advanced since the request was issued.
          if (moveSeqRef.current !== seqAtFetch) {
            cl(`[analytics] Discarding stale strategic flag (moveSeq ${seqAtFetch} → ${moveSeqRef.current})`);
          } else {
            _strategic(!!a.strategic);
            const newRelax = a.strategic ? (a.relax_positions || []) : [];
            // Keep a stable reference when unchanged so the blank-on-arrival effect doesn't re-fire.
            _relaxPositions(prev => (prev.length === newRelax.length && prev.every((v, i) => v === newRelax[i])) ? prev : newRelax);
            if (a.strategic) {
              _magicPattern(null); // strategic supersedes magic pattern (state hygiene)
              changes.push(`strategic relax=[${newRelax.join(',')}]`);
            }
          }
        }
        // Update moveStats — only overwrite fields when the new value is meaningful
        _moveStats(prev => {
          if (!prev) return prev;
          const updated = { ...prev };
          if (a.word_rank > 0) { updated.word_rank = a.word_rank; updated.word_count = a.word_count; }
          if (a.vocab_actual > 0) { updated.vocab_actual = a.vocab_actual; updated.vocab_best = a.vocab_best; updated.vocab_worst = a.vocab_worst; }
          if (a.next_word_count > 0) { updated.next_word_count = a.next_word_count; }
          if (a.word_quartiles && a.word_quartiles.length > 0) { updated.word_quartiles = a.word_quartiles; }
          if (a.mm_actual > 0) {
            updated.mm_actual = a.mm_actual;
            updated.mm_best = a.mm_best;
            updated.mm_worst = a.mm_worst;
            updated.mm_rank = a.mm_rank;
            updated.mm_total = a.mm_total;
          }
          return updated;
        });
        if (a.next_word_count > 0) _nextWordCount(a.next_word_count);
        // Update global stats
        if (a.all_games_avg != null) _allGamesAvg(a.all_games_avg);
        if (a.all_games_mm_avg != null) _allGamesMmGpa(a.all_games_mm_avg);
        if (a.all_games_max_avg != null) _allGamesMaxAvg(a.all_games_max_avg);
        // Update opt_constraints
        if (a.opt_constraints && tristate.current !== 1) {
          _constraints(prev => {
            const nm = a.opt_constraints.min_counts ? new Map(Object.entries(a.opt_constraints.min_counts)) : prev.min_counts;
            const nc = a.opt_constraints.capped || prev.capped;
            const np = a.opt_constraints.pos ? complementUnrestrictedLetters(a.opt_constraints.pos, getUnrestrictedLetters(a.opt_constraints.pos, nm, nc)) : prev.pos;
            return { pos: np, min_counts: nm, capped: nc, prev: null };
          });
        }
        if (a.magic_pattern !== undefined && tristate.current !== 1 && !a.strategic) {
          if (!(a.pending && a.pending.includes('optimized_constraints'))) {
            _magicPattern(a.magic_pattern || null);
          }
        }
        // Update pending display (UI only — does NOT drive polling)
        _pendingAnalytics(a.pending || []);
        // Log
        const resultSummary = changes.length > 0 ? changes.join(', ') : 'no changes';
        const pendingSummary = (a.pending || []).join(',') || 'none';
        const ts = new Date().toISOString().substring(11,23);
        _analyticsDebug(prev => ({ ...prev, lastResult: `[${ts}] +${elapsed}s ${currentTrigger}: ${resultSummary} | pending: ${pendingSummary}` }));
        if (changes.length > 0) {
          _analyticsLog(prev => [{ time: ts, msg: `[+${elapsed}s ${currentTrigger}] ${changes.join(', ')} ✓` }, ...prev].slice(0, 10));
          _wsLogs(prev => [{ time: ts, msg: `[+${elapsed}s ${currentTrigger}] ${changes.join(', ')} ✓` }, ...prev].slice(0, 10));
        }
        // Self-manage poll timer: continue polling if tasks remain, stop when done
        if (!a.pending || a.pending.length === 0) {
          if (analyticsPollTimer.current) { clearInterval(analyticsPollTimer.current); analyticsPollTimer.current = null; }
        } else if (!analyticsPollTimer.current) {
          analyticsPollTimer.current = setInterval(() => fetchMoveAnalytics('poll'), 3000);
        }
      } catch (e) {
        console.log('fetchMoveAnalytics error', e);
      } finally {
        // Release the gate
        analyticsInflight.current = false;
        _analyticsDebug(prev => ({ ...prev, inflight: false }));
        // If a retrigger was queued while we were in-flight, fire it now
        const queued = analyticsRetrigger.current;
        if (queued) {
          analyticsRetrigger.current = null;
          cl(`[analytics] RETRIGGER ${queued}`);
          fetchMoveAnalytics(queued);
        }
      }
    };
    doFetch(trigger);
  };

  // Apply optimized constraints arriving over the WebSocket: set constraints +
  // original_word, refill confirmed greens into the current draft (skipping relaxed
  // slots, read live from relaxPositionsRef to avoid a stale closure), and advance
  // the cursor off a slot that just became green. Shared verbatim by the
  // optimized_constraints and nudge handlers below.
  const applyOptConstraints = (oc) => {
    const complemented_pos = complementUnrestrictedLetters(oc.pos, getUnrestrictedLetters(oc.pos, new Map(Object.entries(oc.min_counts)), oc.capped));
    _constraints({ pos: complemented_pos, min_counts: new Map(Object.entries(oc.min_counts)), capped: oc.capped });
    _original_word(complemented_pos.map(choices => choices.length === 1 ? choices[0] : ' '));
    _word(prev => {
      const updated = [...prev];
      complemented_pos.forEach((choices, idx) => {
        if (choices.length === 1 && prev[idx] !== choices[0] && !relaxPositionsRef.current.includes(idx)) {
          updated[idx] = choices[0];
        }
      });
      return updated;
    });
    _cursor(prev => {
      if (complemented_pos[prev]?.length === 1) {
        for (let i = prev + 1; i < complemented_pos.length; i++) {
          if (complemented_pos[i].length > 1) return i;
        }
        return complemented_pos.length;
      }
      return prev;
    });
  };

  // WebSocket connection (downstream only, ticket-authenticated)
  useEffect(() => {
    if (initializing || isGoogleCallbackRoute) return;
    let ws = null;
    let reconnectTimer = null;
    const connect = async () => {
      try {
        const { data: ticketText } = await apiTracker.current.trackedFetch(origin + "/ws-ticket", { credentials: "include" }, 'ws-ticket');
        const ticket = (ticketText || '').replace(/^"|"$/g, '');
        if (!ticket || ticket === "error") { reconnectTimer = setTimeout(connect, 3000); return; }
        ws = new WebSocket(`${WS_URL}?ticket=${ticket}&api=${apiPrefix}`);
        ws.onmessage = (e) => {
          try {
            const ts = new Date().toISOString().substring(11,23);
            const data = JSON.parse(e.data);
            let summary = e.data;
            if (data.type === 'nudge') summary = `nudge` + (data.opt_constraints ? ' +constraints' : '');
            else if (data.type === 'optimized_constraints') summary = `opt_constraints: mc=${JSON.stringify(data.min_counts)}, capped=${JSON.stringify(data.capped)}`;
            else if (data.type === 'preplay_timing') summary = `timing: new=${data.new_ms}ms, old=${data.old_ms}ms, ${data.secrets}→${data.patterns}`;
            
            _wsLogs(prev => [{ time: ts, msg: summary }, ...prev].slice(0, 10));
            if (data.type === 'preplay_timing') { _wsTiming(data); }
            else if (tristate.current === 1) { 
              if (data.type === 'optimized_constraints') {
                preemptedOptConstraints.current = data;
                preemptedMagicPattern.current = data.magic_pattern;
              } else if (data.type === 'nudge' && data.opt_constraints) {
                preemptedOptConstraints.current = data.opt_constraints;
                if (data.magic_pattern !== undefined) preemptedMagicPattern.current = data.magic_pattern;
              }
              return; 
            } // Skip board mutations while move is inflight but cache state
            else if (data.type === 'optimized_constraints') {
              if (data.magic_pattern !== undefined) {
                _magicPattern(data.magic_pattern);
              } else {
                _magicPattern(null);
              }
              applyOptConstraints(data);
            }
            else if (data.type === 'nudge') {
              // Handle inline opt_constraints if present
              if (data.opt_constraints) {
                applyOptConstraints(data.opt_constraints);
              }
              if (data.magic_pattern !== undefined) {
                _magicPattern(data.magic_pattern);
              } else if (data.opt_constraints) {
                _magicPattern(null);
              }
              // Fetch latest analytics from DB
              fetchMoveAnalytics('nudge');
            }
          } catch (err) { console.log('WS parse error', err); }
        };
        ws.onclose = () => { reconnectTimer = setTimeout(connect, 3000); };
        ws.onerror = (err) => { console.log('WS error', err); ws.close(); };
        wsRef.current = ws;
      } catch (e) { console.log('WS ticket error', e); reconnectTimer = setTimeout(connect, 3000); }
    };
    connect();
    return () => { clearTimeout(reconnectTimer); ws?.close(); };
  }, [initializing, apiPrefix, origin]);

  const wsTimingDisplay = wsTiming ? (
    <div className="ws-timing">
      ⚡ WS: {wsTiming.new_ms}ms new / {wsTiming.old_ms}ms old · {wsTiming.secrets}→{wsTiming.patterns} buckets · {wsTiming.old_ms > 0 && wsTiming.new_ms > 0 ? (wsTiming.old_ms / wsTiming.new_ms).toFixed(1) : '∞'}× speedup
    </div>
  ) : null;

  const copyright = (
    <div className="copyright" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px' }}>
      {/* Build tag is fuzzed per deploy (deploy.sh sets REACT_APP_BUILD_TAG) so the live
          deployment is identifiable. Shows "dev" for un-deployed local builds. */}
      <span>© 2022-2026 prep.ai {process.env.REACT_APP_BUILD_TAG || "dev"}</span>
    </div>
  );

  // Expensive permutation checks — run in a Web Worker for true non-blocking + interruptible computation.
  // On each input change, terminate the old worker (kills mid-recursion) and spawn a new one.
  // eslint-disable-next-line
  const permsWordKey = word.join(',');
  useEffect(() => {
    if (!constraints.pos || !constraints.min_counts) return;
    _permsAsync(prev => ({ ...prev, computing: true }));

    const worker = createPermsWorker();
    worker.onmessage = (e) => {
      _permsAsync({ ...e.data, computing: false });
    };
    worker.onerror = (err) => {
      console.error('permsWorker error:', err);
      _permsAsync(prev => ({ ...prev, computing: false }));
    };
    // Serialize: postMessage can't transfer Maps, so convert to entries array
    worker.postMessage({
      word,
      constraints: {
        pos: constraints.pos,
        capped: constraints.capped,
        min_counts_entries: [...constraints.min_counts.entries()],
      },
      cursor,
      original_word,
      n: game.n,
    });

    return () => worker.terminate(); // true interruption: kills the thread mid-computation
  // eslint-disable-next-line
  }, [permsWordKey, constraints, cursor, original_word]);

  // Auto-player: trigger on toggle or after move completes
  // eslint-disable-next-line
  useEffect(() => {
    if (!autoPlaying || isGoogleCallbackRoute || initializing) {
      if (autoPlayerRef.current) autoPlayerRef.current.stop();
      if (!autoPlaying) _autoPlayStatus('idle');
      return;
    }
    if (game.game_moves && game.game_moves.length >= 1 && game.game_moves[game.game_moves.length - 1].reply === 'G'.repeat(game.n)) {
      _autoPlayStatus('done');
      return;
    }
    if (disabled || tristate.current !== 2) return;
    if (!autoPlayerRef.current) {
      autoPlayerRef.current = new AutoPlayer(origin, apiTracker.current);
    }
    const ap = autoPlayerRef.current;
    if (ap.running) return;
    const timer = setTimeout(() => {
      if (!autoPlaying) return;
      if (!kbRef.current) return;
      const isComp = () => {
        const g = gameRef.current;
        return g.game_moves && g.game_moves.length >= 1 && g.game_moves[g.game_moves.length - 1].reply === 'G'.repeat(g.n);
      };
      ap.start(
        kbRef, gameRef, constraintsRef, autoPlayLevel,
        (status) => _autoPlayStatus(status),
        isComp,
        true
      );
    }, 500);
    return () => clearTimeout(timer);
  });

  // Keep a ref of the latest relax positions so async/WS callbacks (e.g. the opt_constraints
  // green-refill) can skip relaxed slots without a stale closure.
  // eslint-disable-next-line
  useEffect(() => { relaxPositionsRef.current = relaxPositions; }, [relaxPositions]);

  // Relax-model: when relax_positions arrive (a strategic probe is available), blank those slots
  // so the player can type a probe there. Mirrors the server, whose keystroke replay blanks relax
  // slots at init. Functional updater → blanks the CURRENT word, robust to post-submit ordering
  // (init_word fill, then async strategic arrival). Runs once per relax change, so it never wipes
  // letters the player typed afterward.
  // MUST be before early returns (isGoogleCallbackRoute / initializing) to maintain hook order
  // eslint-disable-next-line
  useEffect(() => {
    if (initializing) return;
    if (!relaxPositions || relaxPositions.length === 0) return;
    // A restored draft is already in the server's shape (see draftRestoredRef) — don't touch it.
    if (draftRestoredRef.current) return;
    _word(prev => {
      let changed = false;
      const nw = Array.from(prev);
      // Only pre-filled greens are blanked. A letter in a non-green relax slot was typed by the
      // player before the relax positions arrived, and the server's replay keeps it.
      relaxPositions.forEach(i => { if (nw[i] !== ' ' && constraints.pos[i] && constraints.pos[i].length === 1) { nw[i] = ' '; changed = true; } });
      return changed ? nw : prev;
    });
    // Park the initial cursor on the first EDITABLE slot, which now includes a relaxed green that
    // precedes the first active slot (init_cursor only sees length>1 slots, so it would skip it).
    // Done via a recorded tap so the server's keystroke reconstruction follows (its initial cursor
    // stays normalActive.first()). Only on a fresh draft (nothing typed) and only if it actually
    // moves — otherwise the server rejects a no-op tap and the client/server cursors desync.
    let firstEditable = -1;
    for (let i = 0; i < game.n; i++) {
      if ((constraints.pos[i] && constraints.pos[i].length > 1) || relaxPositions.includes(i)) { firstEditable = i; break; }
    }
    const fresh = word.every((c, i) => c === ' ' || (constraints.pos[i] && constraints.pos[i].length === 1 && c === constraints.pos[i][0]));
    if (fresh && firstEditable >= 0 && firstEditable !== cursor) {
      _cursor(firstEditable);
      last_keystroke_ref.current?._send_keystroke?.(100 + firstEditable);
    }
  }, [relaxPositions]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isGoogleCallbackRoute) {
    return (
      <GoogleAuthCallback
        authOrigin={apiBase}
        onRefreshAuthState={refreshAuthState}
        onNavigate={(nextPath) => { window.location.replace(nextPath || "/"); }}
      />
    );
  }

  if (initializing) {
    return (
      <>
        {copyright}
        <div>initializing</div>
      </>
    );
  }

  // Fetch new game
  const fetch_new_game = async () => {
    if (tristate.current === 1) {
    } else if (tristate.current === 2) {
      console.log("fetching");
      tristate.current = 1;
      _disabled(true);
      try {
        const { data: newGameData } = await apiTracker.current.trackedFetch(origin + "/newgame", { method: "POST", credentials: "include" }, 'newgame');
        const o = newGameData?.o;
        if (!o) throw new Error('newgame returned no data');
        await new Promise(r => setTimeout(r, 9));
        applyGameSnapshot(o, { resetMoveStats: true });
        // Kick off analytics polling for the new game.
        // Clear any poll timer leaked from the previous game/session first.
        if (analyticsPollTimer.current) { clearInterval(analyticsPollTimer.current); analyticsPollTimer.current = null; }
        gameSessionIdRef.current = Date.now();
        analyticsStartTime.current = Date.now();
        fetchMoveAnalytics('load');
        _sharing(false);
        _replaying(false);
        wasComplete.current = false;
        setTimeout(() => {
          if (end_game_feedback_ref.current && game.shared && !game.shared.show) { end_game_feedback_ref.current.focus(); }
          else if (new_game_ref.current) { new_game_ref.current.focus(); }
        }, 34);
        console.log("ok");
      } catch (error) {
        console.log("error");
        if (error.code === error.ABORT_ERR) {
          console.error("aborted fetch");
        } else {
          console.log(error);
        }
      } finally {
        tristate.current = 2;
        _disabled(false);
      }
    } else {
    }
  };

  // Cheap constraint checks (synchronous — O(n))
  const cappedViolations = get_capped_violations(word, constraints);
  const mcViolations = get_mc_violations(word, constraints);
  const unrestrictedLetters = getUnrestrictedLetters(constraints.pos, constraints.min_counts, constraints.capped);
  const ac = new Map();
  word.map(e => { if (! ac.has(e)) ac.set(e, 0); ac.set(e, ac.get(e) + 1); });
  const pendingLetters = [...constraints.min_counts.keys()].filter(k => constraints.min_counts.get(k) > ac.get(k) || ac.get(k) === undefined);

  // Unpack async permutation state for downstream use
  const perms_result = permsAsync.result;
  const shadow_word = (() => {
    const base = permsAsync.shadow.length > 0 ? permsAsync.shadow : (constraints.pos || []).map(p => p.length === 1 ? p[0] : ' ');
    // Relax slots show no green ghost (don't invite the player to "violate" a relaxed green)
    // and must stay blank in the merged word so submit is gated until the player types there
    // — matching the server, which keeps these slots blank until a keystroke fills them.
    return relaxPositions.length ? base.map((c, i) => relaxPositions.includes(i) ? ' ' : c) : base;
  })();
  const permViolations = permsAsync.violations;
  const feasibleLetters = permsAsync.feasible;
  const infeasibleLetters = permsAsync.infeasible;
  const permsComputing = permsAsync.computing;

  const consumePreemptedConstraints = (fallbackPos, fallbackMinCountsEntries, fallbackCapped, applyToState) => {
    const oc = preemptedOptConstraints.current;
    const mp = preemptedMagicPattern.current;
    
    let nm, nc, np;
    let hasPreempted = false;

    if (oc) {
      nm = oc.min_counts ? new Map(Object.entries(oc.min_counts)) : new Map(fallbackMinCountsEntries);
      nc = oc.capped || fallbackCapped;
      np = complementUnrestrictedLetters(oc.pos, getUnrestrictedLetters(oc.pos, nm, nc));
      hasPreempted = true;
    } else {
      nm = new Map(fallbackMinCountsEntries);
      nc = fallbackCapped;
      np = complementUnrestrictedLetters(fallbackPos, getUnrestrictedLetters(fallbackPos, nm, nc));
    }
    
    if (applyToState) {
      _constraints({ pos: np, min_counts: nm, capped: nc, prev: null });
      if (hasPreempted || mp !== undefined) {
        _magicPattern(mp || null);
      }
      preemptedOptConstraints.current = null;
      preemptedMagicPattern.current = undefined;
    }
    
    return { hasPreempted, pos: np, min_counts: nm, capped: nc };
  };
  // (relax-model: no strategic envelope / reachable-set computation — the relax positions are
  // handled directly by the keyboard. Green-violation flagging is suppressed at relax slots, so
  // crystal violations are no longer computed/shown.)

  const kb = new KeyBoard({
    origin, apiTracker: apiTracker.current,
    game, _game, constraints, guessed, guessed_data, feature_unique,
    word, setWord: cacher, cursor, _cursor,
    tristate, _disabled, sharing, _sharing, feeding_back, _feeding_back,
    handle, _handle, share, _share, _vsize,
    clue, _clue, clue_requested, _clue_requested, fetching_clue, _fetching_clue, _suggestion,
    last_key, _last_key, fast,
    shadow_word, infeasibleLetters, pendingLetters, unrestrictedLetters, perms_result, perms_cache,
    strategic, relaxPositions,
    init_word, init_cursor, is_complete,
    end_game_feedback_ref, new_game_ref, last_keystroke_ref,
    _moveStats, _allGamesAvg, _pendingAnalytics, analyticsStartTime, analyticsPollTimer,
    fetchMoveAnalytics, consumePreemptedConstraints,
  });
  kbRef.current = kb;

  // Game logic and rendering
  game.valid_moves = game.game_moves.filter((e) => e.reply !== "Z".repeat(game.n));
  const no_invalids = game.valid_moves.length === game.game_moves.length;

  // Game-wide grade-point averages (D=1..A=4) — see scoring.js (verbatim-extracted, characterization-tested)
  const { gameAvgScore, gameMmScore, gameMaxScore } = computeGameScores(game.valid_moves, game.n);
  let invalids_button = no_invalids ? <></> : <button key='invalids' className={"meta gapped_row"} disabled={sharing || no_invalids} onClick={(e) => { _show_invalids(!show_invalids); }} style={{ gridColumnStart: 5, gridColumnEnd: 6 }}>-</button>
  let digits = <>
    {['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'].map((e, i) => { if (parseInt(e) <= game.n) { return <button key={e} className="meta gapped_row" disabled={kb.disabled_digit(e)} onClick={(_) => kb.digit_dollar(e)} style={{ gridColumnStart: Math.round((20 - game.n) / 2) + i - 1, gridColumnEnd: Math.round((20 - game.n) / 2) + i + 0 }}>{e}</button> } })}
    {['$'].map((e, i) => { if (e === '$' || parseInt(e) <= game.n) { return <button key={e} className="meta gapped_row" disabled={sharing} onClick={(_) => kb.digit_dollar(e)} style={{ gridColumnStart: Math.round((20 - game.n) / 2) + game.n + 0, gridColumnEnd: Math.round((20 - game.n) / 2) + game.n + 1 }}>{e}</button> } })}
  </>;

  if (key_strokes < 5 || key_jumps - (click_jumps + moves) > 5 || (click_jumps + moves) === 0 && key_strokes > 20 || key_strokes > 100) {
    digits = <></>;
  }

  // Feedback, sharing, and buttons
  const on_pre_feedback = async (_) => {
    _feeding_back(true)
    _pre_share([]);
    const { data: preFbData } = await apiTracker.current.trackedFetch(origin + "/preshare", { mode: 'cors', method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) }, 'preshare');
    _pre_share(preFbData?.o || []);
  };

  const on_pre_share = async (_) => {
    _replaying(false);
    _show_invalids(false)
    _sharing(true)
    _hide_i(game.valid_moves.length)
    // Clear the previous game's reservations — a stale list here can hand out
    // the WRONG game's share link (or finalize its pending key).
    _pre_share([]);
    const { data: preShareData } = await apiTracker.current.trackedFetch(origin + "/preshare", { mode: 'cors', method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) }, 'preshare');
    _pre_share(preShareData?.o || []);
  };

  const on_share = async (i, _sharing, _hide_i) => {
    // i is the 0-based row index (valid_moves.length for the compose row);
    // -1 means the clicked row isn't a valid move (invalid Z-row) — ignore.
    // reveal is what the server stores in share.i: the number of moves the
    // recipient sees. Sharing a played row reveals rows 0..i; sharing the
    // compose row reveals every played move.
    if (i < 0) {
      _sharing(false);
      return;
    }
    const reveal = Math.min(i + 1, game.valid_moves.length);
    _hide_i(reveal);
    const available_shares = pre_share.filter(e => e.k.i === reveal && share_entangled === e.k.entangled);
    let url = window.location.origin + "/";
    if (available_shares.length > 0) {
      url += available_shares[0].kk;
    } else {
      const uninitialized_share = pre_share.filter(e => e.k.i === -1)[0];
      if (uninitialized_share === undefined) {
        _sharing(false);
        return;
      }
      const { data } = await apiTracker.current.trackedFetch(origin + "/share", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ k: uninitialized_share.kk, i: reveal, entangled: share_entangled }) }, 'share');
      if (data === null) {
        // Finalize failed — don't hand out a dead link.
        _sharing(false);
        return;
      }
      url += uninitialized_share.kk;
    }
    if (navigator.share !== undefined) {
      navigator.share({ url: url, text: "Let's solve this!", title: "WordChase: A Word Game" }).catch((err) => {
        // Share sheet unavailable/refused (but not user-cancelled): at least
        // put the link on the clipboard.
        if (err?.name !== "AbortError" && navigator.clipboard !== undefined) navigator.clipboard.writeText(url).catch(() => { });
      });
    } else if (navigator.clipboard !== undefined) {
      navigator.clipboard.writeText(url).catch(() => { });
    }
    // Refresh the reservation list so another share in this session gets a
    // fresh pending key instead of re-finalizing a consumed one.
    apiTracker.current.trackedFetch(origin + "/preshare", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) }, 'preshare').then(({ data }) => _pre_share(data?.o || []));
    setTimeout(() => _sharing(false), 333);
  }

  const on_feedback = async (message) => {
    _sending_feedback(true);
    try {
      await apiTracker.current.trackedFetch(origin + "/feedback", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ k: game.shared.share.k, feedback: message }) }, 'feedback');
      setTimeout(() => { if (end_game_feedback_ref.current) end_game_feedback_ref.current.disabled = true; }, 1);
      _feeding_back(false);
      setTimeout(() => { if (new_game_ref.current) new_game_ref.current.focus(); }, 34);
    } catch (error) {
    } finally {
      _sending_feedback(false);
    }
  }

  const on_api_connect = async (options = {}) => {
    const { rethrow = false } = options;
    if (apiConnectState === "pending") return;
    _apiConnectMessage("");
    _apiConnectState("pending");
    try {
      const response = await fetch(`${apiBase}/api/connect`, {
        method: "POST",
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      await response.json().catch(() => null);
      _apiConnectMessage("");
      _apiConnectState("done");
    } catch (error) {
      console.error("api connect failed", error);
      _apiConnectMessage("Could not prepare your session for Google sign-in. Please try again.");
      _apiConnectState("error");
      if (rethrow) {
        throw error;
      }
    }
  };

  const on_oauth = async () => {
    if (oauthState === "pending") return;
    _oauthMessage("");
    _oauthState("pending");
    try {
      await on_api_connect({ rethrow: true });
      const response = await fetch(`${apiBase}/api/auth/google`, {
        method: "POST",
        credentials: "include",
      });
      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.ok || !data?.authUrl) {
        throw new Error(`HTTP ${response.status}`);
      }
      window.location.href = data.authUrl;
    } catch (error) {
      console.error(error);
      _oauthMessage("Google sign-in could not be started. Please try again.");
      _oauthState("error");
    }
  };

  const on_signout = async () => {
    try {
      await fetch(`${apiBase}/api/signout`, {
        method: "POST",
        credentials: "include",
      });
      window.location.reload();
    } catch (error) {
      console.error("signout failed", error);
    }
  };

  // Rendering UI elements
  let caps_class = (caps ? " caps" : "");
  let letter_class = "letter" + caps_class;
  const share_button = (
    <button key='share' disabled={sharing} className="meta svg gapped_row" onClick={on_pre_share} style={{ gridColumnStart: 1, gridColumnEnd: 3 }}>
      <ShareIcon />
    </button>
  );
  const entangle_button = (
    <button key='entangle' disabled={!sharing} className={"meta svg gapped_row sharing" + (!share_entangled ? " dim" : " bright")} onClick={() => _share_entangled(!share_entangled)} style={{ gridColumnStart: 1, gridColumnEnd: 3 }}>
      <img src="infinity-part.png" height="30" />
    </button>
  );
  const enter_button = (
    <button key='enter' className="meta enter gapped_row" disabled={kb.disabled_enter() || disabled} onClick={(_) => kb.enter()} style={{ gridColumnStart: 17, gridColumnEnd: 20 }}>
      <svg height="20" width="20" viewBox="0 0 1024 1024" fill="currentColor"><path stroke="currentColor" d="M864 192c-19.2 0-32 12.8-32 32v224c0 89.6-70.4 160-160 160H236.8l105.6-105.6c12.8-12.8 12.8-32 0-44.8s-32-12.8-44.8 0l-160 160c-3.2 3.2-6.4 6.4-6.4 9.6-3.2 6.4-3.2 16 0 25.6 3.2 3.2 3.2 6.4 6.4 9.6l160 160c6.4 6.4 12.8 9.6 22.4 9.6s16-3.2 22.4-9.6c12.8-12.8 12.8-32 0-44.8L236.8 672H672c124.8 0 224-99.2 224-224V224c0-19.2-12.8-32-32-32z" strokeWidth="100" /></svg>
    </button>
  );
  const stab_button = (
    <button key='stab' className="meta stab gapped_row" disabled={kb.disabled_stab() || disabled} onClick={(_) => kb.stab()} style={{ gridColumnStart: 20, gridColumnEnd: 21 }}>^</button>
  );

  // Keyboard and Compose components
  const keyboard = (
    <div className={"keyboard" + (clue === "" ? " noclue" : " clue")} onClick={(e) => e.target.blur()}>
      {['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'].map((e, i) => <button key={e} className={letter_class + " " + kb.letter_class(e) + " gapped_row"} disabled={false} onClick={(_) => { _touch_strokes(touch_strokes + 1); kb.letter(e); }} style={{ gridColumnStart: i * 2 + 1, gridColumnEnd: i * 2 + 3 }}>{e}</button>)}
      {['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'].map((e, i) => <button key={e} className={letter_class + " " + kb.letter_class(e)} disabled={false} onClick={(_) => { _touch_strokes(touch_strokes + 1); kb.letter(e); }} style={{ gridColumnStart: i * 2 + 1, gridColumnEnd: i * 2 + 3 }}>{e}</button>)}
      <button key='backspace' className="meta" disabled={kb.disabled_backspace()} onClick={(_) => kb.backspace()} style={{ gridColumnStart: 19, gridColumnEnd: 21, gridRowStart: 2, gridRowEnd: 4 }}>⌫</button>
      {['z', 'x', 'c', 'v', 'b', 'n', 'm'].map((e, i) => <button key={e} className={letter_class + " " + kb.letter_class(e)} disabled={false} onClick={(_) => { _touch_strokes(touch_strokes + 1); kb.letter(e); }} style={{ gridColumnStart: i * 2 + 1, gridColumnEnd: i * 2 + 3 }}>{e}</button>)}
      {['.'].map((e, i) => <button key='dot' className={letter_class + " " + kb.letter_class(e)} disabled={false} onClick={(_) => { _touch_strokes(touch_strokes + 1); kb.letter(e); }} style={{ gridColumnStart: 15, gridColumnEnd: 17 }}>{e}</button>)}
      <button key='delete' className="meta enlarge" disabled={kb.disabled_delete()} onClick={(_) => kb.delete()} style={{ gridColumnStart: 17, gridColumnEnd: 19 }}>␡</button>
      {sharing ? entangle_button : share_button}
      <button key='caps' className={(caps ? "" : "caps ") + "meta gapped_row"} disabled={sharing} onClick={toggleCaps} style={{ gridColumnStart: 3, gridColumnEnd: 5 }}>a</button>
      {invalids_button}
      <button key='suggest' className="meta suggest gapped_row" disabled={sharing || is_complete()} onClick={(_) => { _suggestMode(true); _suggestTrigger(t => t + 1); }} style={{ gridColumnStart: 6, gridColumnEnd: 7 }}>*</button>
      <button key='space' className="meta space gapped_row" disabled={kb.disabled_space()} onClick={(_) => kb.space()} style={{ gridColumnStart: 7, gridColumnEnd: 10 }}>_</button>
      <button key='autoplay' className={"meta gapped_row" + (autoPlaying ? " fix_green on" : "")} disabled={sharing} onClick={(_) => {
          if (autoPlaying) {
            _autoPlaying(false);
            if (autoPlayerRef.current) autoPlayerRef.current.stop();
          } else {
            _autoPlaying(true);
          }
        }} style={{ gridColumnStart: 10, gridColumnEnd: 11 }}>@</button>
      <button key='arrow_left' className="meta gapped_row" disabled={kb.disabled_arrow_left()} onClick={(_) => kb.arrow_left()} style={{ gridColumnStart: 11, gridColumnEnd: 12 }}>&lt;</button>
      <button key='escape' className={"meta gapped_row enlarge" + (sharing ? " sharing" : "")} disabled={kb.disabled_escape()} onClick={(_) => kb.escape()} style={{ gridColumnStart: 12, gridColumnEnd: 14 }}>␛</button>
      <button key='arrow_right' className="meta gapped_row" disabled={kb.disabled_arrow_right()} onClick={(_) => kb.arrow_right()} style={{ gridColumnStart: 14, gridColumnEnd: 15 }}>&gt;</button>
      <button key='question' className={"meta question gapped_row" + (fetching_clue ? " paused" : "")} disabled={kb.disabled_question()} onClick={(_) => kb.question()} style={{ gridColumnStart: 16, gridColumnEnd: 17 }}>?</button>
      {enter_button}
      {stab_button}
      {digits}
    </div>
  );

  const noResult = suggestion && /^·+$/.test(suggestion);
  const isSuggestionClickable = suggestMode && !!suggestion && !noResult && !fetching_suggestion && !sharing && !is_complete() && !disabled;
  const on_touch_suggestion = () => {
    if (!isSuggestionClickable) return;
    _touch_strokes(t => t + 1);
    kb.fill_word(suggestion, true);
  };
  const suggestion_div = (suggestMode || suggestion) ? (
    <div
      className={"suggestion" + (fetching_suggestion ? " fading" : "") + (noResult ? " no-result" : "") + (isSuggestionClickable ? " clickable" : "")}
      onClick={isSuggestionClickable ? on_touch_suggestion : undefined}
    >
      {suggestion || "\u00A0"}
    </div>
  ) : null;

  const quartilesArePending = pendingAnalytics.includes('quartile_words');
  const quartilesLoaded = moveStats?.word_quartiles && moveStats.word_quartiles.length > 0;
  const elapsed_s = analyticsStartTime.current ? Math.floor((Date.now() - analyticsStartTime.current) / 1000) : 0;
  const quartile_div = (moveStats || quartilesArePending) ? (
    <div className={"quartile-words" + (quartilesArePending ? " pending" : "")}>
      {quartilesLoaded ? moveStats.word_quartiles.join("·") : (quartilesArePending ? <span>___·___·___·___ <span className="countup">{elapsed_s}s</span></span> : "\u00A0")}
    </div>
  ) : null;

  const clue_div = (
    <div key="clue" className={"clue" + (clue !== "" && !clue_requested ? " blurred-clue" : "")}>{clue}</div>
  );

  const vocabIsPending = pendingAnalytics.includes('vocab_grade');
  const vocabLoaded = moveStats && ((moveStats.vocab_actual || 0) > 0 || (moveStats.word_rank > 0 && moveStats.word_count > 0));
  const vocab_div = (vocabLoaded || vocabIsPending) ? (
    <div className={"analytics-line" + (vocabIsPending ? " pending" : "")}>
      {vocabLoaded
        ? ((moveStats.vocab_actual || 0) > 0
            ? (() => {
                const va = moveStats.vocab_actual, vb = moveStats.vocab_best, vw = moveStats.vocab_worst;
                const logA = Math.log(va).toFixed(2);
                const normVal = vb === vw ? '1.00' : Math.max(0, Math.min(1, (Math.log(vb) - Math.log(va)) / (Math.log(vb) - Math.log(vw)))).toFixed(2);
                return `vocab: freq=${va} [${vw}..${vb}] log:${logA} norm:${normVal}`;
              })()
            : `vocab: ${moveStats.word_rank}/${moveStats.word_count}`)
        : <span>vocab: ···/··· <span className="countup">{elapsed_s}s</span></span>}
    </div>
  ) : null;

  const minimaxIsPending = pendingAnalytics.includes('minimax_grade');
  const minimaxLoaded = moveStats && moveStats.mm_actual > 0;
  const minimax_div = (minimaxLoaded || minimaxIsPending) ? (() => {
    if (minimaxLoaded) {
      const sActual = moveStats.mm_actual;
      const sBest = moveStats.mm_best;
      const sWorst = moveStats.mm_worst;
      const rank = moveStats.mm_rank;
      const total = moveStats.mm_total;
      const logActual = Math.log(sActual).toFixed(2);
      let normVal = "1.00";
      if (sWorst !== sBest) {
        const sWorstLog = Math.log(sWorst);
        const sBestLog = Math.log(sBest);
        let v = (sWorstLog - Math.log(sActual)) / (sWorstLog - sBestLog);
        normVal = Math.max(0, Math.min(1, v)).toFixed(2);
      }
      return (
        <div className="analytics-line">
          minimax: ⚔{sActual} ({rank}/{total}) log:{logActual} norm:{normVal}
        </div>
      );
    } else {
      return (
        <div className="analytics-line pending">
          minimax: ··· (···/···) <span className="countup">{elapsed_s}s</span>
        </div>
      );
    }
  })() : null;

  // Debug version: raw bucket values + best playable comparison (shown in debug pane)
  const strategicGuesses_debug = strategic ? (
    <div className="strategic-guesses strategic-guesses-debug">
      🎯 strategic probe available — relax positions [{relaxPositions.join(', ')}] (word list withheld for integrity)
    </div>
  ) : null;

  // Strategic guesses are never shown to the user (only in debug pane via strategicGuesses_debug).
  // Constraint loosening is visible through teal keyboard coloring and crystal violations.

  const pendingOptConstraints = pendingAnalytics.includes('optimized_constraints');

  const debugPane = (
    <div className={"debug-pane" + (showDebug ? " visible" : "")}>
      <button className="close-debug" onClick={() => _showDebug(false)}>&times;</button>
      <div className="debug-content">
        {wsTimingDisplay}
        <ApiDebugPanel
          tracker={apiTracker.current}
          wsLogs={wsLogs}
          analyticsDebug={analyticsDebug}
          pendingAnalytics={pendingAnalytics}
          moveStats={moveStats}
        />
        {vocab_div}
        {minimax_div}
        {strategicGuesses_debug}
      </div>
    </div>
  );

  const topLeftButtons = !is_viewing ? (
    <AuthButtons
      oauthState={oauthState}
      onOauth={on_oauth}
      onSignout={on_signout}
      apiConnectMessage={apiConnectMessage}
      oauthMessage={oauthMessage}
    />
  ) : null;

  const compose = (
    <>
      {keyboard}
      {quartile_div}
      {suggestion_div}
      {clue_div}
      <Compose
        i={game.valid_moves.length}
        n={game.n}
        hide_n={game.valid_moves.some(e => e.guess.search("  ") === -1)}
        cursor={cursor}
        word={word}
        shadow_word={shadow_word}
        move={(i) => { if (constraints.pos[i].length > 1) { _click_jumps(click_jumps + 1); } kb.tap(i); }}
        disabled={disabled}
        guessed_data={guessed_data}
        exhausted={exhausted}
        fast={fast}
        last_key={last_key}
        _last_key={_last_key}
        sharing={sharing}
        onShare={(i) => on_share(i, _sharing, _hide_i)}
        cappedViolations={cappedViolations}
        permViolations={permViolations}
        crystalViolations={[]}
        strategicMode={strategic}
        nextWordCount={moveStats?.next_word_count ?? nextWordCount}
        pendingOptConstraints={permsComputing || pendingAnalytics.includes("optimized_constraints")}
        magicPattern={
          magicPattern &&
          !strategic &&
          !pendingAnalytics.includes("minimax_grade") &&
          !pendingAnalytics.includes("strategic_guesses")
            ? magicPattern
            : null
        }
      />
    </>
  );
  const inactive_compose = <Compose i={game.valid_moves.length} n={game.n} hide_n={game.valid_moves.some(e => e.guess.search("  ") === -1)} cursor={cursor} word={word} shadow_word={shadow_word} move={(i) => { }} disabled={disabled} guessed_data={guessed_data} exhausted={exhausted} fast={fast} last_key={last_key} _last_key={_last_key} sharing={sharing} onShare={(i) => { }} cappedViolations={cappedViolations} permViolations={permViolations} crystalViolations={[]} strategicMode={false} nextWordCount={moveStats?.next_word_count ?? nextWordCount} pendingOptConstraints={permsComputing} magicPattern={null} />
  const end_game_feedback = (
    <button key='end_game_feedback' ref={end_game_feedback_ref} disabled={(game.shared && game.shared.show) || feeding_back || sharing} className="end_game minor left" onClick={(_) => { on_pre_feedback(); setTimeout(() => { if (end_game_escape_feedback_ref.current) { end_game_escape_feedback_ref.current.focus(); } }, 0); }}>
      <ShareIcon mirrored />
    </button>
  );
  const end_game_escape_feedback = <button key='end_game_escape_feedback' ref={end_game_escape_feedback_ref} className="end_game minor left" disabled={kb.disabled_escape()} onClick={(_) => kb.escape()}>&times;</button>
  const end_game_share = (
    <button key='end_game_share' disabled={sharing || feeding_back} className="end_game minor" onClick={(_) => { on_pre_share(); setTimeout(() => { if (end_game_escape_share_ref.current) { end_game_escape_share_ref.current.focus(); } }, 0); }}>
      <ShareIcon />
    </button>
  );
  const feedback_emoji = (
    <>
      <button key="feedback_emoji_1" className="feedback_emoji" disabled={sending_feedback} onClick={() => on_feedback('🙂')}>🙂</button>
      <button key="feedback_emoji_2" className="feedback_emoji" disabled={sending_feedback} onClick={() => on_feedback('😐')}>😐</button>
      <button key="feedback_emoji_3" className="feedback_emoji" disabled={sending_feedback} onClick={() => on_feedback('😥')}>😥</button>
      <button key="feedback_emoji_4" className="feedback_emoji" disabled={sending_feedback} onClick={() => on_feedback('😒')}>😒</button>
    </>
  );
  let feedback_message = <></>;
  let has_title = false;
  let title = <></>;
  const title_width = game.n * (game.n > 7 ? 38 : 48);
  if (game.shared && is_complete()) {
    feedback_message = <label key="feedback_message" className="end_game minor feedback left" disabled={false}>{game.shared.feedback}</label>
    title = <div className="title" style={{width: title_width + "px"}}><div className="handle"><span className="at">@</span>{handle}</div><div className="share"><span className="slash">/</span>{game.shared.share.k}</div></div>
    has_title = true;
  } else if (!is_viewing && share != "" && is_complete()) {
    title = <div className="title" style={{width: title_width + "px"}}><div className="handle"><span className="at">@</span>{handle}</div><div className="share"><span className="slash">/</span>{share}</div></div>
    has_title = true;
  }
  const end_game_escape_share = <button key='end_game_escape_share' ref={end_game_escape_share_ref} className="end_game minor" disabled={kb.disabled_escape()} onClick={(_) => kb.escape()}>&times;</button>
  const replay_game = is_complete() && !is_viewing ? (
    <button key="replay_game" className="end_game minor left" style={{ backgroundColor: "#3b3b3b", color: "white" }} onClick={() => _replaying(!replaying)} title={replaying ? "Stop Replay" : "Replay"} disabled={disabled || sharing}>
      {replaying ? (
        <span style={{ fontSize: "1.5rem", lineHeight: "1" }}>&times;</span>
      ) : (
        <svg height="20" width="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
          <path d="M3 3v5h5"></path>
        </svg>
      )}
    </button>
  ) : null;
  const new_game = <button key="new_game" ref={new_game_ref} className="end_game" onClick={fetch_new_game} disabled={disabled || sharing}>&#x2192;</button>;
  const end_game_question = <button key='end_game_question' disabled={fetching_clue || (clue !== "" && clue_requested)} className={"end_game minor question" + (fetching_clue ? " paused" : "")} onClick={(_) => kb.question()} style={{ right: "-45px", backgroundColor: "#3b3b3b", color: "white" }}>?</button>;
  const end_game = (
    <div key="end_game_div" className="end_game_div">
      {game.shared ? (feeding_back ? end_game_escape_feedback : (game.shared.show ? feedback_message : end_game_feedback)) : replay_game}
      {feeding_back ? feedback_emoji : new_game}
      {sharing ? end_game_escape_share : end_game_share}
      {end_game_question}
    </div>
  );
  const invalid = (show_invalids && game.game_moves.length !== game.valid_moves.length && !game.shared && share == "") ? (
    <div key="invalid" className="invalid">
      {game.game_moves.filter((e) => e.reply === "Z".repeat(game.n)).map((e, i) => <Move i={0} last={false} n={game.n} first_pos={[...e.guess.matchAll(/[^\s]/g)][0]?.index ?? 0} last_pos={last([...e.guess.matchAll(/[^\s]/g)])?.index ?? (game.n - 1)} key={"invalid_" + e.guess} guess={e.guess} reply={e.reply} seq={0} rep={0} vsize={-1} invalid={true} _show_invalids={_show_invalids} is_complete={is_complete} sharing={false} onShare={(i) => on_share(i, _sharing, _hide_i)} line_over_first={false} clue="" clue_shown={false} clue_requested={false} gameMaxScore={null} allGamesMaxAvg={null} gameAvgScore={null} gameMmScore={null} allGamesAvg={null} allGamesMmAvg={null} />)}
    </div>
  ) : null;
  const valid_or_invalid_moves = Array.from((is_complete() ? game.valid_moves : (show_invalids ? game.game_moves : game.valid_moves)));
  const moves_to_show = valid_or_invalid_moves.slice(0, (row_count < 0 || row_count > valid_or_invalid_moves.length) ? valid_or_invalid_moves.length : row_count);
  const bottom = is_viewing ? [<div key="spacer" className="spacer" />, (is_complete() ? null : (row_count == -1 || row_count > moves_to_show.length ? inactive_compose : null))] : (is_complete() ? [invalid, clue_div, quartile_div, end_game] : compose);
  const top_spacer = <div className="spacer" />;
  const sliver = <div className="sliver" />;

  return (
    <div onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column-reverse', alignItems: 'center', gap: '1px' }}>
      {topLeftButtons}
      {debugPane}
      {copyright}
      {bottom}
      <div className="moves-container" style={{ display: 'flex', flexDirection: 'column-reverse', flex: 1, overflowY: 'auto', width: '100%', alignItems: 'center', gap: '1px' }}>
        {replaying ? (
          <GameReplay game={game} vsize={vsize} gameMaxScore={gameMaxScore} allGamesMaxAvg={allGamesMaxAvg} gameAvgScore={gameAvgScore} gameMmScore={gameMmScore} allGamesAvg={allGamesAvg} allGamesMmAvg={allGamesMmGpa} onReplayEnd={() => _replaying(false)} />
        ) : (
          moves_to_show.reverse().map((e, i) => (
            <Move
              i={game.valid_moves.length - i - 1}
              last={i === 0}
              n={game.n}
              first_pos={[...e.guess.matchAll(/[^\s]/g)][0]?.index ?? 0}
              last_pos={last([...e.guess.matchAll(/[^\s]/g)])?.index ?? (game.n - 1)}
              key={"guess_" + i}
              guess={sharing ? (hide_i <= game.valid_moves.length - i - 1 ? " ".repeat(game.n) : e.guess) : e.guess}
              reply={e.reply}
              seq={game.valid_moves[game.valid_moves.length - i - 0] === undefined ? e.seq : (e.seq + 1 == game.valid_moves[game.valid_moves.length - i - 0].seq ? "." : e.seq)}
              rep={e.rep}
              vsize={i === 0 ? vsize : -1}
              invalid={e.reply === "Z".repeat(game.n)}
              _show_invalids={_show_invalids}
              is_complete={is_complete}
              sharing={sharing}
              onShare={() => on_share(game.valid_moves.findIndex((m) => m === e), _sharing, _hide_i)}
              line_over_first={is_viewing && row_count < 0 || !is_viewing && has_title}
              clue={e.wflr_clue.clue}
              clue_shown={e.wflr_clue.clue_shown}
              clue_requested={e.wflr_clue.clue_requested}
              word_rank={e.word_rank || 0}
              word_count={e.word_count || 0}
              vocab_actual={e.vocab_actual || 0}
              vocab_best={e.vocab_best || 0}
              vocab_worst={e.vocab_worst || 0}
              mm_actual={e.mm_actual || 0}
              mm_best={e.mm_best || 0}
              mm_worst={e.mm_worst || 0}
              gameMaxScore={gameMaxScore}
              allGamesMaxAvg={allGamesMaxAvg}
              gameAvgScore={gameAvgScore}
              gameMmScore={gameMmScore}
              allGamesAvg={allGamesAvg}
              allGamesMmAvg={allGamesMmGpa}
            />
          ))
        )}
        {is_viewing ? null : title}
        {is_viewing && row_count == -1 ? sliver : top_spacer}
      </div>
    </div>
  );
}

function cl (msg) { console.log(msg); }

export default App;
